import { useMemo, useRef, type MutableRefObject } from 'react';
import { Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import type { FieldErrors, UseFormProps } from 'react-hook-form';
import type { AnyObjectSchema } from 'yup';

import type { LeaseDetailsFormConfig } from './config';
import { buildLeaseDetailsSchema } from './lease-details.schema';
import { buildInitialFormValues, type LeaseDetailsFormValues } from './lease-details.defaults';
import { LeaseDetailsDateLinkingContext } from './lease-details-date-linking.context';
import { LeaseDetailsSubmitLockProvider } from './lease-details-submit-lock.context';
import { LeaseDetailsUIProvider } from './lease-details-ui.context';
import { SectionsRenderer } from './sections-renderer';
import { FormActions } from './form-actions';
import { FormLifecycleReporter, type FormLifecycleCallbacks } from './form-lifecycle-reporter';

// Form engine shared by renter invite, deal lease details, edit invite and renewal lease details.
// The engine never branches on consumer identity — all per-surface behavior lives in the
// `LeaseDetailsFormConfig` the caller passes in. See `config/` for the shape.

export type { LeaseDetailsFormValues };

export type LeaseDetailsFormProps = FormLifecycleCallbacks & {
  config: LeaseDetailsFormConfig;
  /**
   * Hydration map for an edit flow. If omitted, each field falls back to its
   * `FieldConfig.defaultValue`. The `applicants` array is padded up to
   * `applicantsConfig.min` regardless of what's provided here.
   */
  initialValues?: Partial<LeaseDetailsFormValues>;
  /**
   * Overrides the config-derived schema. Migration aid so a consumer can keep
   * its existing handwritten schema while adopting the engine; dropped once
   * validation is fully config-derived (P4.3).
   */
  schema?: AnyObjectSchema;
  /**
   * RHF validation mode. Defaults to `'all'` (validate on change and on blur), which is how every
   * consumer behaved before the inputs honored `mode`.
   */
  mode?: UseFormProps['mode'];
  /** RHF `shouldUnregister`. Defaults to `false`. */
  shouldUnregister?: UseFormProps['shouldUnregister'];
  /** Fires after `config.submit.submit()` resolves. */
  onSuccess?: (result: unknown, values: LeaseDetailsFormValues) => void;
  /** Fires when `config.submit.submit()` rejects. */
  onError?: (error: unknown, values: LeaseDetailsFormValues) => void;
  /**
   * Fires when a submit is blocked by client validation (RHF's invalid
   * branch). Carries the field errors and current values for analytics.
   */
  onValidationError?: (
    errors: FieldErrors<LeaseDetailsFormValues>,
    values: LeaseDetailsFormValues,
  ) => void;
  /**
   * Fires on cancel-button click. The engine doesn't unmount itself; the
   * consumer dismisses its outer chrome (drawer close, accordion collapse).
   */
  onCancel?: () => void;
  /**
   * External ref for the "user manually edited the end date" signal that
   * gates start→end auto-derivation. When provided, the engine reads/writes
   * this ref instead of its own internal one — letting a parent keep the
   * flag alive across form unmount/remount (e.g. cancel→edit cycles).
   */
  endDateManuallyEditedRef?: MutableRefObject<boolean>;
};

// Wrap in a `LocalizationProvider` (MUI date pickers) and ensure an
// `AnalyticsContext` is available if any `events` callback calls `track()`.
export function LeaseDetailsForm({
  config,
  initialValues,
  schema: providedSchema,
  mode = 'all',
  shouldUnregister = false,
  onSuccess,
  onError,
  onValidationError,
  onCancel,
  onDirtyChange,
  onUnmountDirty,
  endDateManuallyEditedRef: externalEndDateManuallyEditedRef,
}: LeaseDetailsFormProps) {
  const schema = useMemo(
    () => providedSchema ?? buildLeaseDetailsSchema(config),
    [providedSchema, config],
  );
  // RHF only reads `defaultValues` on mount; consumers keep `config` and `initialValues` stable.
  const defaultValues = useMemo(
    () => buildInitialFormValues(config, initialValues),
    [config, initialValues],
  );

  const methods = useForm<LeaseDetailsFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode,
    shouldUnregister,
  });

  // Incremented on every submit attempt — valid and invalid — so consumers can
  // report a stable attempt number (e.g. invite's `submit_attempt_number`).
  const submitAttemptRef = useRef(0);
  // Shared mutable flag for linked-date behavior (`start -> end` auto-fill).
  // Caller-supplied ref wins, so parents can preserve the flag across
  // form unmount/remount; otherwise the engine owns a fresh local ref.
  const internalEndDateManuallyEditedRef = useRef(false);
  const endDateManuallyEditedRef =
    externalEndDateManuallyEditedRef ?? internalEndDateManuallyEditedRef;

  // Inner-section spacing isn't carried at the form level — each section
  // publishes its own via `SectionShell`. Form-level spacing (`betweenSections`)
  // is consumed directly inside `SectionsRenderer`.
  const dateLinkingContextValue = useMemo(
    () => ({ config: config.dateLinking, endDateManuallyEdited: endDateManuallyEditedRef }),
    [config.dateLinking, endDateManuallyEditedRef],
  );

  const uiContextValue = useMemo(
    () => ({
      inputSize: config.ui?.inputSize ?? ('medium' as const),
      asteriskColor: config.ui?.asteriskColor,
    }),
    [config.ui?.inputSize, config.ui?.asteriskColor],
  );

  const handleSubmit = methods.handleSubmit(
    async (values) => {
      submitAttemptRef.current += 1;
      try {
        const payload = config.submit.buildPayload(values);
        const result = await config.submit.submit(payload, {
          values,
          setError: methods.setError,
          attemptNumber: submitAttemptRef.current,
        });
        onSuccess?.(result, values);
      } catch (error) {
        methods.setError('root', { type: 'submit', message: 'Submission failed' });
        onError?.(error, values);
      }
    },
    (errors) => {
      submitAttemptRef.current += 1;
      onValidationError?.(errors, methods.getValues());
    },
  );

  const actionsVariant = config.ui?.actions?.variant ?? 'inline';
  const showActions = !config.ui?.hideDefaultActions;
  const inlineActions = showActions && actionsVariant === 'inline';
  const stickyActions = showActions && actionsVariant === 'sticky';

  return (
    <FormProvider {...methods}>
      <LeaseDetailsDateLinkingContext.Provider value={dateLinkingContextValue}>
        <LeaseDetailsUIProvider value={uiContextValue}>
          <LeaseDetailsSubmitLockProvider>
            <form onSubmit={handleSubmit} noValidate={true} id="lease-details-form">
              <Stack direction="column" spacing={config.ui?.contentSpacing ?? 3}>
                {config.ui?.slots?.top}
                <SectionsRenderer config={config} />
                {inlineActions ? (
                  <FormActions
                    submitLabel={config.ui?.submitButtonLabel ?? 'Submit'}
                    cancelLabel={config.ui?.cancelButtonLabel ?? 'Cancel'}
                    actions={config.ui?.actions}
                    onCancel={onCancel}
                  />
                ) : null}
              </Stack>
              {/* Sticky actions and the generic `slots.bottom` are rendered outside
                the content Stack — direct `<form>` children — so their sticky
                positioning and full-bleed negative margins extend to the drawer
                edges (flex `align-items: stretch` swallows them in the Stack). */}
              {stickyActions ? (
                <FormActions
                  submitLabel={config.ui?.submitButtonLabel ?? 'Submit'}
                  cancelLabel={config.ui?.cancelButtonLabel ?? 'Cancel'}
                  actions={config.ui?.actions}
                  onCancel={onCancel}
                />
              ) : null}
              {config.ui?.slots?.bottom}
            </form>
            {onDirtyChange || onUnmountDirty ? (
              <FormLifecycleReporter
                onDirtyChange={onDirtyChange}
                onUnmountDirty={onUnmountDirty}
              />
            ) : null}
          </LeaseDetailsSubmitLockProvider>
        </LeaseDetailsUIProvider>
      </LeaseDetailsDateLinkingContext.Provider>
    </FormProvider>
  );
}
